import React from 'react';
import { QrCode, Scissors } from 'lucide-react';
import type { PrintCommand } from './printingApi';

/**
 * Ένα χαρτί της προεπισκόπησης, από τις ΙΔΙΕΣ εντολές που στέλνει ο server στον agent (POST
 * /printing/preview). Μιμείται τον agent σε «Εικόνα» (print-agent/lib/imageRenderer.js): γράμματα
 * σταθερού πλάτους, 42 θέσεις στα 80 mm / 32 στα 58 mm, «double» 1,7× και έντονο, ύψος γραμμής 1,5,
 * κενό («feed») 0,6 γραμμής, διαχωριστικό = λεπτή γραμμή, QR στο 40% του πλάτους, λογότυπο ως 60%.
 * Ό,τι δεν χωρά σε μία γραμμή στριμώχνεται οριζόντια, όπως ο agent (fillText με maxWidth).
 * Το χαρτί είναι πάντα λευκό με μαύρα γράμματα — και στο σκούρο θέμα: έτσι βγαίνει στον εκτυπωτή.
 */

interface Props {
  commands: PrintCommand[];
  paperWidth: 58 | 80;
  /** Το λογότυπο της φόρμας (data-URI): ο server στέλνει μόνο το σήμα 'logo'. */
  logo: string | null;
  /** «Έντονα γράμματα στα δελτία»: ο agent «φουσκώνει» κάθε γραμμή — εδώ ένα λεπτό περίγραμμα. */
  heavy?: boolean;
  label: string;
  /** Το πλάτος που χωρά (στενή οθόνη): το χαρτί μικραίνει αναλογικά, ίδιες θέσεις ανά γραμμή. */
  maxWidth?: number;
}

const PAPER_PX: Record<58 | 80, number> = { 80: 300, 58: 218 };
const CHARS: Record<58 | 80, number> = { 80: 42, 58: 32 };
const DOUBLE = 1.7;
const FONT = '"Lucida Console", Consolas, "Courier New", monospace';

// Πλάτος ενός γράμματος ως κλάσμα του μεγέθους, ΜΕΤΡΗΜΕΝΟ στη γραμματοσειρά που έχει ο browser (Lucida
// Console 0,6, Consolas 0,55…): με σταθερό 0,6 οι γραμμές των 42 θέσεων έβγαιναν από το χαρτί. Χωρίς
// canvas (tests) 0,6.
// Τα έντονα μετρούν χωριστά: η Lucida Console δεν έχει έντονα και ο browser τα «φουσκώνει» πλατύτερα.
const charEm: Record<'normal' | 'bold', number | null> = { normal: null, bold: null };
function measuredCharEm(weight: 'normal' | 'bold'): number {
  const known = charEm[weight];
  if (known !== null) return known;
  let em = 0.6;
  try {
    const ctx = typeof document !== 'undefined' && typeof HTMLCanvasElement !== 'undefined'
      && !/jsdom/i.test(typeof navigator !== 'undefined' ? navigator.userAgent : '')
      ? document.createElement('canvas').getContext('2d') : null;
    if (ctx) {
      ctx.font = `${weight === 'bold' ? 'bold ' : ''}100px ${FONT}`;
      const w = ctx.measureText('0'.repeat(20)).width / 2000;
      if (w > 0.3 && w < 1) em = w;
    }
  } catch {
    // Χωρίς canvas — μένει η προεπιλογή.
  }
  charEm[weight] = em;
  return em;
}

const PAPER_PAD = 12;

const TicketPaper: React.FC<Props> = ({ commands, paperWidth, logo, heavy = false, label, maxWidth }) => {
  const width = Math.max(160, Math.min(PAPER_PX[paperWidth], (maxWidth ?? Infinity) - PAPER_PAD * 2));
  const chars = CHARS[paperWidth];
  const em = measuredCharEm('normal');
  const emBold = measuredCharEm('bold');
  const fs = width / (chars * em);
  const line = fs * 1.5;

  /** Μία γραμμή κειμένου: στριμώχνεται αν δεν χωρά (όπως ο agent), αλλιώς όπως είναι. */
  const fit = (text: string, size: number, align: 'left' | 'center' | 'right', bold: boolean, key: number) => {
    // +2% περιθώριο: στρογγυλέματα και το λεπτό περίγραμμα των «Έντονων».
    const natural = text.length * size * (bold ? emBold : em) * 1.02;
    const squeeze = natural > width ? width / natural : 1;
    const origin = align === 'center' ? 'center' : align === 'right' ? 'right' : 'left';
    return (
      <div key={key} style={{ textAlign: align, fontSize: size, lineHeight: `${Math.max(line, size * 1.2)}px`, fontWeight: bold ? 700 : 400, whiteSpace: 'pre', overflow: 'hidden' }}>
        <span style={{ display: 'inline-block', transform: squeeze < 1 ? `scaleX(${squeeze})` : undefined, transformOrigin: origin }}>
          {text || ' '}
        </span>
      </div>
    );
  };

  const items: React.ReactNode[] = [];
  commands.forEach((c, i) => {
    const content = typeof c.content === 'string' ? c.content : '';
    switch (c.type) {
      case 'text': items.push(fit(content, fs, 'left', false, i)); break;
      case 'bold': items.push(fit(content, fs, 'left', true, i)); break;
      case 'center': items.push(fit(content.trim(), fs, 'center', false, i)); break;
      case 'bold_center': items.push(fit(content.trim(), fs, 'center', true, i)); break;
      case 'right': items.push(fit(content.trim(), fs, 'right', false, i)); break;
      case 'small': items.push(fit(content, fs * 0.82, 'left', false, i)); break;
      case 'small_center': items.push(fit(content.trim(), fs * 0.82, 'center', false, i)); break;
      case 'double': items.push(fit(content, fs * DOUBLE, 'left', true, i)); break;
      case 'double_center': items.push(fit(content.trim(), fs * DOUBLE, 'center', true, i)); break;
      case 'left_right':
        items.push(
          <div key={i} className="flex justify-between gap-2" style={{ fontSize: fs, lineHeight: `${line}px`, whiteSpace: 'pre' }}>
            <span className="min-w-0 overflow-hidden">{c.left || ''}</span>
            <span className="shrink-0">{c.right || ''}</span>
          </div>,
        );
        break;
      case 'divider':
        items.push(<div key={i} style={{ height: line, display: 'flex', alignItems: 'center' }}><div className="w-full border-t border-black" /></div>);
        break;
      case 'feed':
        items.push(<div key={i} style={{ height: line * 0.6 }} />);
        break;
      case 'qr':
        items.push(
          <div key={i} className="flex justify-center" style={{ padding: `${line * 0.3}px 0` }}>
            <QrCode aria-hidden="true" style={{ width: width * 0.4, height: width * 0.4 }} strokeWidth={1.4} />
          </div>,
        );
        break;
      case 'image':
      case 'logo':
        items.push(
          <div key={i} className="flex justify-center" style={{ padding: `${line * 0.2}px 0` }}>
            {logo
              ? <img src={logo} alt="" className="object-contain grayscale" style={{ maxWidth: width * 0.6, maxHeight: width * 0.45 }} />
              : <span className="border border-dashed border-neutral-400 px-3 py-1 text-[11px] text-neutral-500">LOGO</span>}
          </div>,
        );
        break;
      case 'cut':
        items.push(
          <div key={i} className="flex items-center gap-1.5 text-neutral-400 mt-1" aria-hidden="true">
            <Scissors className="w-3.5 h-3.5" /><div className="flex-1 border-t border-dashed border-neutral-400" />
          </div>,
        );
        break;
      default:
        // beep (μπιπ) και ό,τι άλλο: τίποτα στο χαρτί.
        break;
    }
  });

  return (
    <figure className="m-0" aria-label={label}>
      <div
        className="mx-auto bg-white text-black rounded-[3px] shadow-[0_1px_2px_rgba(0,0,0,0.12),0_6px_20px_rgba(0,0,0,0.10)] ring-1 ring-black/5"
        style={{
          width: width + PAPER_PAD * 2,
          padding: `14px ${PAPER_PAD}px 10px`,
          fontFamily: FONT,
          WebkitTextStroke: heavy ? '0.45px currentColor' : undefined,
        }}
        data-testid="ticket-paper"
        data-paper={paperWidth}
      >
        {items}
      </div>
    </figure>
  );
};

export default TicketPaper;
